import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Minus, Plus, RotateCcw } from 'lucide-react';
import { formatMoney, rowBurialType } from '../../data/cemeteries';
import { PLOT_STATUS, getPlot } from '../../lib/plotStore';

/**
 * The interactive site layout.
 *
 * Rows are drawn top-to-bottom on screen but lettered bottom-to-top on the
 * survey — row A is the southernmost row, nearest the swale, so it renders
 * LAST. Plot numbers run left to right, 1 at the west edge.
 */

const BURIAL_LABEL = { burial: 'Burial', cremation: 'Cremation' };

const STATUS_STYLES = {
  [PLOT_STATUS.AVAILABLE]: {
    burial:
      'bg-white border-slate-300 text-slate-500 hover:border-navy-700 hover:bg-navy-50 hover:text-navy-800',
    cremation:
      // emerald-300 on emerald-50 was 1.44:1 — the site numbers were effectively
      // invisible. emerald-700 (the tone hover already used) is 5.2:1, and hover
      // still reads through the border and background change.
      'bg-emerald-50 border-emerald-200 text-emerald-700 hover:border-emerald-500 hover:bg-emerald-100 hover:text-emerald-700',
  },
  [PLOT_STATUS.SELECTED]: {
    burial: 'bg-navy-800 border-navy-900 text-white shadow-sm',
    cremation: 'bg-navy-800 border-navy-900 text-white shadow-sm',
  },
  [PLOT_STATUS.HELD]: {
    burial: 'bg-amber-100 border-amber-400 text-amber-700 cursor-not-allowed',
    cremation: 'bg-amber-100 border-amber-400 text-amber-700 cursor-not-allowed',
  },
  [PLOT_STATUS.PENDING]: {
    burial: 'bg-slate-100 border-slate-300 text-slate-600 cursor-not-allowed',
    cremation: 'bg-slate-100 border-slate-300 text-slate-600 cursor-not-allowed',
  },
  [PLOT_STATUS.SOLD]: {
    burial: 'bg-slate-200 border-slate-200 text-slate-600 cursor-not-allowed',
    cremation: 'bg-slate-200 border-slate-200 text-slate-600 cursor-not-allowed',
  },
};

const STATUS_LABELS = {
  [PLOT_STATUS.AVAILABLE]: 'Available',
  [PLOT_STATUS.SELECTED]: 'Selected by you',
  [PLOT_STATUS.HELD]: 'On hold by another family',
  [PLOT_STATUS.PENDING]: 'Reserved — sale pending',
  [PLOT_STATUS.SOLD]: 'Sold',
};

const isSelectable = (status) =>
  status === PLOT_STATUS.AVAILABLE || status === PLOT_STATUS.SELECTED;

const LEGEND = [
  { label: 'Available — burial', className: 'bg-white border-slate-300' },
  { label: 'Available — cremation', className: 'bg-emerald-50 border-emerald-300' },
  { label: 'Your selection', className: 'bg-navy-800 border-navy-900' },
  { label: 'On hold', className: 'bg-amber-100 border-amber-400' },
  { label: 'Sale pending', className: 'bg-slate-100 border-slate-300' },
  { label: 'Sold', className: 'bg-slate-200 border-slate-200' },
];

const ZOOM_LEVELS = [0.7, 0.85, 1, 1.25, 1.5];

const PlotMap = ({ cemetery, onPlotClick, onError }) => {
  const [zoomIndex, setZoomIndex] = useState(2);
  const [hovered, setHovered] = useState(null); // { plot, rect }
  const scrollRef = useRef(null);

  const zoom = ZOOM_LEVELS[zoomIndex];
  const { rows, plotsPerRow, pathsAbove } = cemetery.layout;

  // Rows render top-of-map first, so reverse the bottom-to-top survey order.
  const displayRows = useMemo(() => [...rows].reverse(), [rows]);
  const columns = useMemo(
    () => Array.from({ length: plotsPerRow }, (_, i) => i + 1),
    [plotsPerRow],
  );

  const handleEnter = useCallback((event, plot) => {
    setHovered({ plot, rect: event.currentTarget.getBoundingClientRect() });
  }, []);

  const handleLeave = useCallback(() => setHovered(null), []);

  const handleClick = useCallback(
    (plot) => {
      if (!isSelectable(plot.status)) {
        onError?.(
          plot.status === PLOT_STATUS.SOLD
            ? `Site ${plot.id} has already been sold.`
            : plot.status === PLOT_STATUS.PENDING
              ? `Site ${plot.id} is reserved pending a sale.`
              : `Site ${plot.id} is on hold by another family right now.`,
        );
        return;
      }
      onPlotClick(plot);
    },
    [onPlotClick, onError],
  );

  const cellWidth = Math.round(22 * zoom);
  const cellHeight = Math.round(28 * zoom);

  return (
    <div className="relative">
      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          {LEGEND.map((item) => (
            <div key={item.label} className="flex items-center gap-2">
              <span
                className={`inline-block w-4 h-5 rounded-sm border ${item.className}`}
                aria-hidden="true"
              />
              <span className="text-xs text-slate-600">{item.label}</span>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setZoomIndex((i) => Math.max(0, i - 1))}
            disabled={zoomIndex === 0}
            className="p-2 rounded border border-slate-300 text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent"
            aria-label="Zoom out"
          >
            <Minus className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => setZoomIndex(2)}
            className="p-2 rounded border border-slate-300 text-slate-600 hover:bg-slate-100"
            aria-label="Reset zoom"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => setZoomIndex((i) => Math.min(ZOOM_LEVELS.length - 1, i + 1))}
            disabled={zoomIndex === ZOOM_LEVELS.length - 1}
            className="p-2 rounded border border-slate-300 text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent"
            aria-label="Zoom in"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Map */}
      <div
        ref={scrollRef}
        className="overflow-x-auto rounded-lg border border-slate-200 bg-slate-50 p-4 sm:p-6"
      >
        <div className="inline-block min-w-full">
          {/* North edge */}
          <p className="text-center text-[10px] uppercase tracking-[0.2em] text-slate-500 pb-3">
            North — Ex. 22 Drive &amp; Columbarium
          </p>

          <div role="grid" aria-label={`${cemetery.name} site layout`}>
            {displayRows.map((row) => {
              const type = rowBurialType(cemetery, row);
              const isCremation = type === 'cremation';
              const hasPathAbove = pathsAbove.includes(row);

              return (
                <React.Fragment key={row}>
                  {hasPathAbove && (
                    <div
                      className="my-2 h-3 rounded-sm bg-slate-200/70 border-y border-dashed border-slate-300"
                      aria-hidden="true"
                    />
                  )}

                  <div
                    role="row"
                    className={`flex items-center gap-2 py-0.5 rounded ${
                      isCremation ? 'bg-emerald-100/40' : ''
                    }`}
                  >
                    <span
                      className="w-6 shrink-0 text-right font-cormorant text-base font-semibold text-slate-500"
                      aria-hidden="true"
                    >
                      {row}
                    </span>

                    <div className="flex gap-[2px]">
                      {columns.map((number) => {
                        const plot = getPlot(cemetery, `${row}-${number}`);
                        const selectable = isSelectable(plot.status);
                        const styles = STATUS_STYLES[plot.status][type];

                        // Sold sites are inert — nothing to say, and keeping
                        // them out of the tab order spares keyboard users a
                        // long walk. Held and pending sites stay clickable so
                        // they can explain *why* they can't be taken.
                        const inert = plot.status === PLOT_STATUS.SOLD;

                        return (
                          <button
                            key={plot.id}
                            type="button"
                            role="gridcell"
                            disabled={inert}
                            aria-disabled={!selectable}
                            onClick={() => handleClick(plot)}
                            onMouseEnter={(e) => handleEnter(e, plot)}
                            onMouseLeave={handleLeave}
                            onFocus={(e) => handleEnter(e, plot)}
                            onBlur={handleLeave}
                            style={{ width: cellWidth, height: cellHeight }}
                            className={`shrink-0 rounded-[3px] border text-[9px] font-medium leading-none transition-colors duration-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-700 focus-visible:ring-offset-1 ${styles}`}
                            aria-label={`Site ${plot.id}, row ${row}, ${
                              BURIAL_LABEL[plot.burialType]
                            }, ${formatMoney(plot.price)}, ${STATUS_LABELS[plot.status]}`}
                            title={`${plot.id} · ${BURIAL_LABEL[plot.burialType]} · ${formatMoney(
                              plot.price,
                            )} · ${STATUS_LABELS[plot.status]}`}
                          >
                            {zoom >= 1 ? number : ''}
                          </button>
                        );
                      })}
                    </div>

                    <span
                      className="w-6 shrink-0 font-cormorant text-base font-semibold text-slate-500"
                      aria-hidden="true"
                    >
                      {row}
                    </span>
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          {/* South edge */}
          <p className="text-center text-[10px] uppercase tracking-[0.2em] text-slate-500 pt-3">
            South — Existing Vegetated Swale
          </p>
        </div>
      </div>

      <p className="mt-3 text-xs text-slate-500">
        Rows are lettered <strong>A</strong> at the southern edge through <strong>N</strong> at the
        north. Site numbers run <strong>1–{plotsPerRow}</strong>, west to east. Rows{' '}
        {cemetery.layout.cremationRows.join(' and ')} form the cremation garden.
      </p>

      {hovered && <PlotTooltip plot={hovered.plot} rect={hovered.rect} />}
    </div>
  );
};

/** Floating detail card, anchored above the hovered site. */
const PlotTooltip = ({ plot, rect }) => {
  const style = {
    position: 'fixed',
    left: Math.min(Math.max(rect.left + rect.width / 2, 110), window.innerWidth - 110),
    top: rect.top - 12,
    transform: 'translate(-50%, -100%)',
    zIndex: 60,
  };

  const unavailable =
    plot.status !== PLOT_STATUS.AVAILABLE && plot.status !== PLOT_STATUS.SELECTED;

  return (
    <div
      style={style}
      role="tooltip"
      className="pointer-events-none w-52 rounded-lg border border-slate-200 bg-white p-3 shadow-xl"
    >
      <div className="flex items-baseline justify-between">
        <span className="font-cormorant text-xl font-semibold text-navy-900">
          Site {plot.id}
        </span>
        <span className="text-[10px] uppercase tracking-widest text-slate-500">
          Row {plot.row} · No. {plot.number}
        </span>
      </div>

      <dl className="mt-2 space-y-1 text-xs">
        <div className="flex justify-between gap-3">
          <dt className="shrink-0 text-slate-500">Type</dt>
          <dd className="text-right font-medium text-slate-800">
            {BURIAL_LABEL[plot.burialType]}
          </dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="shrink-0 text-slate-500">Price</dt>
          <dd className="text-right font-medium text-slate-800">{formatMoney(plot.price)}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="shrink-0 text-slate-500">Status</dt>
          <dd
            className={`text-right font-medium ${
              unavailable ? 'text-slate-500' : 'text-navy-800'
            }`}
          >
            {STATUS_LABELS[plot.status]}
          </dd>
        </div>
      </dl>

      {!unavailable && (
        <p className="mt-2 border-t border-slate-100 pt-2 text-[11px] text-slate-500">
          {plot.status === PLOT_STATUS.SELECTED
            ? 'Click to release this site.'
            : 'Click to hold this site for 15 minutes.'}
        </p>
      )}
    </div>
  );
};

export default PlotMap;
